import {useMemo} from 'react';
import {ErrorNotice, Loading} from './components';
import type {HermesState, NativeTokenSnapshot} from './lib/types';
import type {TeamMemberView} from './team-workspace';
import {StorageUsage} from './storage-usage';
import {hermesActions} from './lib/store';
import './workspace-usage.css';

const tokenNumber = new Intl.NumberFormat(undefined, {maximumFractionDigits: 0});
const dollars = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2});

function currentTokens(state: HermesState): NativeTokenSnapshot | null {
  const snapshot = state.nativeTokens.data;
  return state.nativeTokens.status === 'ready' && state.nativeConnection.status === 'open'
    && snapshot?.sessionId === state.nativeTarget?.sessionId ? snapshot : null;
}

export function ConversationCost({state, sessionId}: {state: HermesState; sessionId: string | null}) {
  const credit = state.accountBalance;
  if (credit.status === 'error' || credit.data?.status === 'ready') return <section className="workspace-balance" aria-label="Account credits">
    <ErrorNotice error={credit.error}/>
    {credit.data?.status === 'ready' && <strong>{dollars.format(credit.data.availableUsd)} credits</strong>}
    <Loading active={credit.status === 'loading'}/>
  </section>;
  const current = currentTokens(state);
  const estimate = current?.sessionId === sessionId ? current.costEstimate : null;
  return <section className="workspace-balance" aria-label="Estimated cost for this chat"><span>Estimated cost</span>
    <ErrorNotice error={state.nativeTokens.error}/>
    {estimate?.status === 'error' && <ErrorNotice error={estimate.error}/>}
    {estimate?.status === 'ready' ? <strong title={`Recorded usage in this chat and its bots, at published standard token rates checked ${estimate.rateDate}. Includes normal input, cache reads, cache writes, and output. Other model tools use Hermes's recorded cost. Context and processing surcharges and memory-service calls are not included. Updates after reported usage changes.`}>{dollars.format(estimate.costUsd)}</strong>
      : <Loading active={state.nativeTokens.status === 'loading' && state.nativeConnection.status === 'open'}/>}
  </section>;
}

export function BotSessionTokens({state, member}: {state: HermesState; member: TeamMemberView}) {
  const snapshot = currentTokens(state);
  const usage = snapshot?.agents.find(agent => agent.sessionId === member.sessionId);
  if (!usage) return null;
  const description = `${tokenNumber.format(usage.inputTokens)} input tokens and ${tokenNumber.format(usage.outputTokens)} output tokens reported in this chat. The current model response is not counted until OpenAI reports its usage.`;
  return <span className="bot-session-tokens" title={usage.costEstimate.status === 'error'
    ? `${description}\n${usage.costEstimate.error.message}\n${usage.costEstimate.error.stack}` : description}>
      {tokenNumber.format(usage.totalTokens)} tokens
      {usage.costEstimate.status === 'ready' && <> / {dollars.format(usage.costEstimate.costUsd)}</>}
  </span>;
}

function ConversationTokens({state, sessionId}: {state: HermesState; sessionId: string | null}) {
  const current = currentTokens(state);
  const snapshot = current?.sessionId === sessionId ? current : null;
  const totals = useMemo(() => {
    if (!snapshot) return null;
    return snapshot.agents.reduce((sum, agent) => ({
      input: sum.input + agent.inputTokens,
      output: sum.output + agent.outputTokens,
    }), {input: 0, output: 0});
  }, [snapshot]);
  return <section className="conversation-token-usage" aria-label="Token use in this chat">
    <ErrorNotice error={state.nativeTokens.error}/>
    {state.nativeTokens.status === 'error' && state.nativeConnection.status === 'open' &&
      <button className="text-button" type="button" onClick={hermesActions.reconnectNativeTokens}>Reconnect usage</button>}
    {totals ? <>
      <strong className="conversation-token-total">{tokenNumber.format(totals.input + totals.output)} total</strong>
      <dl className="conversation-token-breakdown">
        <div><dt>Input</dt><dd>{tokenNumber.format(totals.input)}</dd></div>
        <div><dt>Output</dt><dd>{tokenNumber.format(totals.output)}</dd></div>
      </dl>
    </> : <Loading active={state.nativeTokens.status === 'loading' && state.nativeConnection.status === 'open'}/>}
  </section>;
}

export function WorkspaceUsage({state, sessionId}: {state: HermesState; sessionId: string | null}) {
  return <div className="workspace-usage" aria-label="Storage and tokens">
    <StorageUsage state={state} compact/>
    <ConversationTokens state={state} sessionId={sessionId}/>
  </div>;
}
